import { useState } from 'react'
import { ItemPickerDropdown } from './item-picker-dropdown'
import { fmtUnit } from '@/lib/format-unit'
import { useLineItemRow } from '@/hooks/use-line-item-row'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

const CoinsIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>
  </svg>
)

const fmt = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

interface MiscSectionProps {
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  section?: string
  serviceFilter?: string
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number; hideFromProposal?: boolean }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

export function MiscSection({ lineItems, pricingItems, section, serviceFilter, onAddItem, onUpdateItem, onDeleteItem }: MiscSectionProps) {
  const miscItems = pricingItems.filter((p) => {
    if (p.category !== 'miscellaneous' && p.category !== 'misc') return false
    if (!serviceFilter) return true
    if (!p.service) return false
    return p.service.split(',').map(s => s.trim()).includes(serviceFilter)
  })
  const rows = lineItems.filter((li) => li.category === 'misc' && li.section === (section || null))


  const handleSelect = async (item: PricingItem) => {
    // Use per-service rate if available, otherwise fall back to base rate
    const svcRates = serviceFilter && item.serviceRates?.[serviceFilter]
    const effectiveRate = svcRates ? svcRates.rate : item.rate
    await onAddItem({
      description: item.name,
      category: 'misc',
      quantity: 1,
      unit: item.unit || 'ea',
      rate: effectiveRate,
      pricingItemId: item.id,
      section: section,
      // Misc costs are internal by default — the estimator opts each one INTO
      // the proposal via the "Show in Proposal" checkbox.
      hideFromProposal: true,
    })
  }

  const handleFieldChange = async (li: LineItem, updates: Record<string, number>) => {
    // lineTotal from the row's full current field set (never a stale store field).
    const m = { ...li, ...updates }
    const lineTotal = (m.quantity || 0) * m.rate
    await onUpdateItem(li.id, { quantity: m.quantity, rate: m.rate, lineTotal })
  }

  const sectionTotal = rows.reduce((sum, li) => sum + li.quantity * li.rate, 0)

  return (
    <div className="sc-section">
      <div className="sc-section-header">
        <CoinsIcon />
        <span className="sc-section-title">Miscellaneous Costs</span>
        <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
          <ItemPickerDropdown items={miscItems} onSelect={handleSelect} serviceFilter={serviceFilter} />
        </div>
      </div>

      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Cost/Unit</th>
              <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Show in Proposal</th>
              <th className="col-right">Total</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((li) => (
              <MiscRow
                key={li.id}
                item={li}
                onFieldChange={handleFieldChange}
                onDelete={() => onDeleteItem(li.id)}
                onRename={(id, description) => onUpdateItem(id, { description })}
                onHideToggle={(id, hide) => onUpdateItem(id, { hideFromProposal: hide })}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} className="col-right"><strong>Total</strong></td>
                <td className="col-right"><span className="sc-foot-total">{fmt(sectionTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function MiscRow({
  item, onFieldChange, onDelete, onRename, onHideToggle,
}: {
  item: LineItem
  onFieldChange: (li: LineItem, updates: Record<string, number>) => Promise<void>
  onDelete: () => void
  onRename: (itemId: string, description: string) => Promise<void>
  onHideToggle: (itemId: string, hide: boolean) => void
}) {
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(item.description)
  const [rateEditing, setRateEditing] = useState(false)

  const { values, setField, onFocus, onBlur } = useLineItemRow(
    item,
    (it) => ({ quantity: it.quantity, rate: it.rate }),
    (v) => { onFieldChange(item, v) },
  )
  const qty = values.quantity
  const rate = values.rate

  return (
    <tr>
      <td className="col-name">
        {editingName ? (
          <input
            className="sc-input"
            style={{ minWidth: '120px' }}
            value={nameValue}
            autoFocus
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => { setEditingName(false); onRename(item.id, nameValue) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setEditingName(false); onRename(item.id, nameValue) } if (e.key === 'Escape') { setEditingName(false); setNameValue(item.description) } }}
          />
        ) : (
          <span className="sc-input-rename" title="Click to rename" onClick={() => setEditingName(true)}>{nameValue}</span>
        )}
      </td>
      <td><input type="number" className="sc-input" value={qty || ''}
        onChange={(e) => setField('quantity', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td>{fmtUnit(item.unit)}</td>
      <td><input type={rateEditing ? 'number' : 'text'} className="sc-input"
        value={rateEditing ? (rate || '') : ('$' + rate.toFixed(2))}
        onChange={(e) => setField('rate', parseFloat(e.target.value) || 0)}
        onFocus={() => { setRateEditing(true); onFocus() }}
        onBlur={() => { setRateEditing(false); onBlur() }} onDoubleClick={selectAll} min={0} /></td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={!item.hideFromProposal}
          onChange={(e) => onHideToggle(item.id, !e.target.checked)}
          title="Show this item on the proposal"
        />
      </td>
      <td className="col-right"><strong>{fmt(qty * rate)}</strong></td>
      <td className="col-action">
        <button onClick={onDelete} className="sc-icon-btn" title="Delete"><DeleteIcon /></button>
      </td>
    </tr>
  )
}
